import React, { useEffect, useRef, useState } from "react";
import { X, Check, ArrowRight, Handshake, MapPin } from "lucide-react";
import { brand } from "../mock/content";
import { t } from "../lib/ui";
import { track } from "../lib/analytics";

const API = process.env.REACT_APP_BACKEND_URL;
const ESPERIENZA = ["Commerciale", "Tecnico-commerciale", "Agente di commercio", "Segnalatore", "Altro"];

export default function PartnerCommercialeModal({ open, onClose }) {
  const [show, setShow] = useState(false);
  const [form, setForm] = useState({ nome: "", azienda: "", provincia: "", telefono: "", email: "", esperienza: "Commerciale", messaggio: "" });
  const [hp, setHp] = useState("");
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) { setShow(false); return; }
    const raf = requestAnimationFrame(() => setShow(true));
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      cancelAnimationFrame(raf);
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  if (!open) return null;

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.nome || !form.telefono || !form.email || !form.provincia) { setError(t("pc_error")); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          tipo: "Partner Commerciale",
          nome: form.nome,
          azienda: form.azienda || null,
          indirizzo: form.provincia,
          esperienza: form.esperienza,
          telefono: form.telefono,
          email: form.email,
          messaggio: form.messaggio || null,
          hp,
          source: "Contatti · Partner Commerciale",
        }),
      });
      if (!res.ok) throw new Error("bad");
      setSent(true);
      track("generate_lead", { form_type: "Partner Commerciale", source: "Contatti · Partner Commerciale" });
      setTimeout(() => onClose(), 3500);
    } catch {
      setError(t("pc_error"));
    } finally {
      setLoading(false);
    }
  };

  const inp = "w-full bg-white/[0.04] border border-white/15 px-3.5 py-2.5 text-sm text-white rounded focus:border-[#F9E212] outline-none placeholder:text-white/30";
  const lbl = "block text-white/50 text-xs uppercase tracking-[0.14em] mb-1.5";

  return (
    <div className="fixed inset-0 z-[130] overflow-y-auto overscroll-contain" data-testid="pc-modal" role="dialog" aria-modal="true">
      <div className={`fixed inset-0 bg-black/85 backdrop-blur-md transition-opacity duration-300 ${show ? "opacity-100" : "opacity-0"}`} onClick={onClose} />
      <div className="relative min-h-full flex items-center justify-center p-4">
        <div className={`relative w-[min(560px,96vw)] bg-[#0a0a0a] border border-white/10 rounded-xl overflow-hidden transition-all duration-300 ${show ? "opacity-100 translate-y-0 scale-100" : "opacity-0 translate-y-4 scale-[0.98]"}`}>
          <button onClick={onClose} aria-label="Chiudi" data-testid="pc-close" className="absolute top-3 right-3 z-10 w-9 h-9 flex items-center justify-center rounded-full bg-black/60 border border-white/20 text-white/70 hover:text-white hover:border-white/50 transition-colors">
            <X size={18} />
          </button>

          <div className="p-6 md:p-8">
            {sent ? (
              <div className="flex flex-col items-center justify-center text-center py-10" data-testid="pc-success">
                <div className="w-14 h-14 rounded-full flex items-center justify-center mb-4" style={{ background: brand.yellow }}>
                  <Check size={26} className="text-black" />
                </div>
                <p className="text-white text-lg font-semibold" style={{ fontFamily: "Archivo" }}>{t("pc_modal_title")}</p>
                <p className="text-white/60 text-sm mt-2 max-w-xs">{t("pc_success")}</p>
              </div>
            ) : (
              <>
                <div className="flex items-center gap-3 mb-1.5">
                  <span className="w-10 h-10 flex items-center justify-center rounded-lg shrink-0" style={{ background: brand.yellow }}>
                    <Handshake size={20} className="text-black" />
                  </span>
                  <h2 className="text-white font-black text-xl md:text-2xl tracking-tight" style={{ fontFamily: "Archivo" }}>{t("pc_modal_title")}</h2>
                </div>
                <p className="text-white/60 text-sm leading-relaxed mb-5">{t("pc_modal_sub")}</p>

                <form onSubmit={submit} className="space-y-3.5" data-testid="pc-form">
                  <input type="text" name="website" tabIndex={-1} autoComplete="off" value={hp} onChange={(e) => setHp(e.target.value)} className="hidden" aria-hidden="true" />

                  <div>
                    <label className={lbl}>{t("pc_nome")} *</label>
                    <input data-testid="pc-nome" className={inp} value={form.nome} onChange={update("nome")} required />
                  </div>
                  <div>
                    <label className={lbl}>{t("pc_azienda")}</label>
                    <input data-testid="pc-azienda" className={inp} value={form.azienda} onChange={update("azienda")} />
                  </div>
                  <div>
                    <label className={lbl}>{t("pc_provincia")} *</label>
                    <PcProvincia value={form.provincia} onChange={(v) => setForm((f) => ({ ...f, provincia: v }))} className={inp} placeholder={t("pc_provincia_ph")} />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className={lbl}>{t("pc_tel")} *</label>
                      <input data-testid="pc-telefono" type="tel" className={inp} value={form.telefono} onChange={update("telefono")} required />
                    </div>
                    <div>
                      <label className={lbl}>{t("pc_email")} *</label>
                      <input data-testid="pc-email" type="email" className={inp} value={form.email} onChange={update("email")} required />
                    </div>
                  </div>
                  <div>
                    <label className={lbl}>{t("pc_esperienza")}</label>
                    <select data-testid="pc-esperienza" className={`${inp} appearance-none cursor-pointer`} value={form.esperienza} onChange={update("esperienza")}>
                      {ESPERIENZA.map((o) => <option key={o} value={o} className="bg-[#0a0a0a]">{o}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className={lbl}>{t("pc_messaggio")}</label>
                    <textarea data-testid="pc-messaggio" rows={3} className={inp} value={form.messaggio} onChange={update("messaggio")} />
                  </div>

                  {error && <p className="text-red-400 text-xs" data-testid="pc-error">{error}</p>}
                  <button type="submit" disabled={loading} data-testid="pc-submit" className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 text-black text-sm uppercase tracking-[0.12em] font-semibold rounded transition-transform hover:scale-[1.02] disabled:opacity-60" style={{ background: brand.yellow, fontFamily: "Space Grotesk" }}>
                    {loading ? "Invio…" : t("pc_submit")} <ArrowRight size={16} />
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function PcProvincia({ value, onChange, className, placeholder }) {
  const [q, setQ] = useState(value || "");
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const timer = useRef(null);
  const token = useRef(Math.random().toString(36).slice(2));

  useEffect(() => { setQ(value || ""); }, [value]);

  const onInput = (val) => {
    setQ(val);
    onChange(val);
    if (timer.current) clearTimeout(timer.current);
    if (val.trim().length < 3) { setItems([]); setOpen(false); return; }
    timer.current = setTimeout(async () => {
      try {
        const res = await fetch(`${API}/api/geo/autocomplete?q=${encodeURIComponent(val)}&session_token=${token.current}`);
        const data = await res.json();
        setItems(data.items || []);
        setOpen((data.items || []).length > 0);
      } catch { setItems([]); }
    }, 350);
  };

  const pick = (it) => { onChange(it.label); setQ(it.label); setItems([]); setOpen(false); };

  return (
    <div className="relative">
      <div className="relative">
        <MapPin size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/60" />
        <input
          data-testid="pc-provincia"
          className={`${className} pl-9`}
          placeholder={placeholder}
          value={q}
          autoComplete="off"
          onChange={(e) => onInput(e.target.value)}
          onFocus={() => items.length && setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          required
        />
      </div>
      {open && items.length > 0 && (
        <ul className="absolute z-30 left-0 right-0 mt-1 bg-[#141414] border border-white/15 rounded shadow-lg max-h-56 overflow-auto" data-testid="pc-provincia-suggestions">
          {items.map((it, i) => (
            <li key={it.place_id || i}>
              <button type="button" onMouseDown={(e) => { e.preventDefault(); pick(it); }} data-testid={`pc-provincia-suggestion-${i}`} className="w-full text-left px-3.5 py-2 text-xs text-white/80 hover:bg-white/10 transition-colors flex items-start gap-2">
                <MapPin size={13} className="mt-0.5 shrink-0 text-[#F9E212]" /> {it.label}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
